import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Upload, Image as ImageIcon, Loader2, Send, UserCheck, Clock, AlertCircle, Search, Plus, Edit2, Calendar } from 'lucide-react';
import { employeeApi } from '../../api/employeeApi';
import { specialtyApi } from '../../api/specialtyApi';

import { employeeAvailabilityApi } from '../../api/employeeAvailabilityApi';
import type { EmployeeResponse } from '../../api/employeeApi';
import type { SpecialtyResponse } from '../../api/specialtyApi';
import { useSalonId } from '../../hooks/useSalonId';
import { useSubscription } from '../../hooks/useSubscription';
import { useToast } from '../../contexts/ToastContext';
import { useUpsellModal } from '../../components/UpsellModal';
import EmployeeAvailabilityManager, { type AvailabilityOverride } from '../../components/ui/EmployeeAvailabilityManager';
import PrerequisiteAlertModal from '../../components/ui/PrerequisiteAlertModal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { TableSkeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { uploadApi } from '../../api/uploadApi';

const EmployeeListPage = () => {
    const salonId = useSalonId();
    const { getLimit, isOwner, isManager } = useSubscription();
    const toast = useToast();
    const { showUpsell } = useUpsellModal();
    const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
    const [filteredEmployees, setFilteredEmployees] = useState<EmployeeResponse[]>([]);
    const [specialties, setSpecialties] = useState<SpecialtyResponse[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Create/Edit State
    const [showModal, setShowModal] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState<EmployeeResponse | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const [selectedSpecialtyIds, setSelectedSpecialtyIds] = useState<string[]>([]);

    // Availability State
    const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
    const [selectedEmployeeForAvailability, setSelectedEmployeeForAvailability] = useState<EmployeeResponse | null>(null);
    const [availabilities, setAvailabilities] = useState<AvailabilityOverride[]>([]);
    const [showPrerequisiteAlert, setShowPrerequisiteAlert] = useState(false);

    // Invitation State
    const [invitingEmployeeId, setInvitingEmployeeId] = useState<string | null>(null);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        role: 'STYLIST',
        commissionPercentage: '15',
        salonId: salonId || '',
        isActive: true
    });

    const [photoUrl, setPhotoUrl] = useState<string | null>(null);
    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        if (!salonId) return;
        loadEmployees();
        loadSpecialties();
    }, [salonId]);

    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredEmployees(employees);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = employees.filter(emp =>
            emp.firstName.toLowerCase().includes(query) ||
            emp.lastName.toLowerCase().includes(query) ||
            emp.specialties.some(s => s.name.toLowerCase().includes(query))
        );
        setFilteredEmployees(filtered);
    }, [searchQuery, employees]);

    const loadEmployees = async () => {
        if (!salonId) return;
        setIsLoading(true);
        try {
            const data = await employeeApi.getSalonEmployees(salonId);
            if (!Array.isArray(data)) {
                setEmployees([]);
                setFilteredEmployees([]);
                return;
            }

            const rolePriority: Record<string, number> = {
                'OWNER': 1, 'MANAGER': 2, 'STYLIST': 3, 'RECEPTIONIST': 4, 'ASSISTANT': 5
            };

            const sortedData = data.sort((a, b) => {
                const priorityA = rolePriority[a.role] || 99;
                const priorityB = rolePriority[b.role] || 99;
                return priorityA !== priorityB ? priorityA - priorityB : a.firstName.localeCompare(b.firstName, 'es', { sensitivity: 'base' });
            });

            setEmployees(sortedData);
            setFilteredEmployees(sortedData);
        } catch (error) {
            console.error('Error loading employees:', error);
            setEmployees([]);
            setFilteredEmployees([]);
        } finally {
            setIsLoading(false);
        }
    };

    const loadSpecialties = async () => {
        if (!salonId) return;
        try {
            const data = await specialtyApi.getSalonSpecialties(salonId);
            setSpecialties(data);
        } catch (error) {
            console.error('Error loading specialties:', error);
        }
    };

    const handleCreateClick = () => {
        if (specialties.length === 0) {
            setShowPrerequisiteAlert(true);
            return;
        }
        const activeEmployees = employees.filter(e => e.isActive).length;
        const employeeLimit = getLimit('employees');

        if (activeEmployees >= employeeLimit) {
            showUpsell('EMPLOYEE_LIMIT');
            return;
        }

        setEditingEmployee(null);
        setFormData({
            firstName: '', lastName: '', phone: '', email: '', role: 'STYLIST',
            commissionPercentage: '15', salonId: salonId || '', isActive: true
        });
        setPhotoUrl(null);
        setPhotoFile(null);
        setPhotoPreview(null);
        setSelectedSpecialtyIds([]);
        setShowModal(true);
    };

    const handleEditClick = (employee: EmployeeResponse) => {
        setEditingEmployee(employee);
        setFormData({
            firstName: employee.firstName,
            lastName: employee.lastName,
            phone: employee.phone || '',
            email: employee.email || '',
            role: employee.role,
            commissionPercentage: employee.commissionPercentage.toString(),
            salonId: employee.salonId,
            isActive: employee.isActive
        });
        setPhotoUrl(employee.photoUrl || null);
        setPhotoPreview(employee.photoUrl || null);
        setPhotoFile(null);
        setSelectedSpecialtyIds(employee.specialties.map(s => s.id));
        setShowModal(true);
    };

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setPhotoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setPhotoPreview(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const uploadPhoto = async (): Promise<string | null> => {
        if (!photoFile || !salonId) return photoUrl;
        setIsUploading(true);
        try {
            return await uploadApi.uploadFile(photoFile, salonId, 'employees');
        } catch (err) {
            console.error('Photo upload error:', err);
            toast.error('Error al subir la foto');
            throw new Error('Error al subir la foto');
        } finally {
            setIsUploading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitError(null);

        // --- VALIDATION START ---
        if (!formData.firstName.trim()) {
            setSubmitError('El nombre es obligatorio');
            setIsSubmitting(false);
            return;
        }
        if (!formData.lastName.trim()) {
            setSubmitError('El apellido es obligatorio');
            setIsSubmitting(false);
            return;
        }
        if (formData.role === 'STYLIST') {
            if (!formData.commissionPercentage || isNaN(parseFloat(formData.commissionPercentage))) {
                setSubmitError('La comisión debe ser un número válido');
                setIsSubmitting(false);
                return;
            }
            if (parseFloat(formData.commissionPercentage) < 0 || parseFloat(formData.commissionPercentage) > 100) {
                setSubmitError('La comisión debe estar entre 0 y 100');
                setIsSubmitting(false);
                return;
            }
        }
        // --- VALIDATION END ---

        try {
            let finalPhotoUrl = photoUrl;
            if (photoFile) finalPhotoUrl = await uploadPhoto();

            // Incluir especialidades y comisión según el rol
            const payload: any = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone || undefined,
                email: formData.email || undefined,
                salonId: formData.salonId,
                role: formData.role as any,
                isActive: formData.isActive,
                photoUrl: finalPhotoUrl || undefined,
                // Especialidades siempre se envían (vacío si no es estilista)
                specialtyIds: formData.role === 'STYLIST' ? selectedSpecialtyIds : [],
                // Comisión solo si es estilista, sino 0
                commissionPercentage: formData.role === 'STYLIST' ? parseFloat(formData.commissionPercentage) : 0
            };


            if (editingEmployee) {
                await employeeApi.updateEmployee(editingEmployee.id, payload);
                toast.success('Empleado actualizado exitosamente');
            } else {
                await employeeApi.createEmployee(payload as any);
                toast.success('Empleado creado exitosamente');
            }

            setShowModal(false);
            loadEmployees();
        } catch (error: any) {
            console.error('Error saving employee:', error);
            if (error.response?.status === 403 || error.message?.includes('límite')) {
                setShowModal(false);
                showUpsell('EMPLOYEE_LIMIT');
                return;
            }
            setSubmitError(error.response?.data?.message || 'Error al guardar empleado');
        } finally {
            setIsSubmitting(false);
        }
    };

    const toggleSpecialty = (specialtyId: string) => {
        if (selectedSpecialtyIds.includes(specialtyId)) {
            setSelectedSpecialtyIds(selectedSpecialtyIds.filter(id => id !== specialtyId));
        } else {
            setSelectedSpecialtyIds([...selectedSpecialtyIds, specialtyId]);
        }
    };

    const handleAvailabilityClick = async (employee: EmployeeResponse) => {
        setSelectedEmployeeForAvailability(employee);
        try {
            const data = await employeeAvailabilityApi.getEmployeeAvailability(employee.id);
            const mappedAvailabilities: AvailabilityOverride[] = data.map(item => ({
                id: item.id,
                startDate: new Date(item.startDate),
                endDate: new Date(item.endDate),
                type: item.type,
                startTime: item.startTime,
                endTime: item.endTime,
                notes: item.notes
            }));
            setAvailabilities(mappedAvailabilities);
            setShowAvailabilityModal(true);
        } catch (error) {
            console.error('Error fetching availability:', error);
            toast.error('Error al cargar la disponibilidad');
        }
    };

    const handleAddAvailabilityOverride = async (override: Omit<AvailabilityOverride, 'id'>) => {
        if (!selectedEmployeeForAvailability) return;
        try {
            const newItem = await employeeAvailabilityApi.createAvailabilityOverride({
                employeeId: selectedEmployeeForAvailability.id,
                type: override.type,
                startDate: format(override.startDate, 'yyyy-MM-dd'),
                endDate: format(override.endDate, 'yyyy-MM-dd'),
                startTime: override.startTime,
                endTime: override.endTime,
                notes: override.notes
            });

            const newOverride: AvailabilityOverride = {
                id: newItem.id,
                startDate: new Date(newItem.startDate),
                endDate: new Date(newItem.endDate),
                type: newItem.type,
                startTime: newItem.startTime,
                endTime: newItem.endTime,
                notes: newItem.notes
            };
            setAvailabilities([...availabilities, newOverride]);
            toast.success('Ausencia registrada exitosamente');
        } catch (error) {
            console.error('Error creating availability override:', error);
            toast.error('Error al crear la ausencia');
        }
    };

    const handleDeleteAvailabilityOverride = async (id: string) => {
        if (!selectedEmployeeForAvailability) return;
        try {
            await employeeAvailabilityApi.deleteAvailabilityOverride(selectedEmployeeForAvailability.id, id);
            setAvailabilities(availabilities.filter(a => a.id !== id));
            toast.success('Ausencia eliminada exitosamente');
        } catch (error) {
            console.error('Error deleting availability override:', error);
            toast.error('Error al eliminar la ausencia');
        }
    };

    const handleInviteEmployee = async (employee: EmployeeResponse) => {
        if (!employee.email) {
            toast.error('El empleado debe tener un email registrado para poder invitarlo al sistema.');
            return;
        }

        setInvitingEmployeeId(employee.id);

        try {
            const response = await employeeApi.inviteEmployee(employee.id);
            toast.success(response.message);
            loadEmployees();
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Error al enviar la invitación';
            toast.error(errorMessage);
        } finally {
            setInvitingEmployeeId(null);
        }
    };

    const getInvitationBadge = (employee: EmployeeResponse) => {
        if (employee.hasUserAccount) {
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                    <UserCheck className="w-3.5 h-3.5" />
                    Usuario Activo
                </span>
            );
        }

        switch (employee.invitationStatus) {
            case 'PENDING':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-100">
                        <Clock className="w-3.5 h-3.5" />
                        Pendiente
                    </span>
                );
            case 'EXPIRED':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-red-50 text-red-700 border border-red-100">
                        <AlertCircle className="w-3.5 h-3.5" />
                        Expirado
                    </span>
                );
            case 'NOT_INVITED':
            default:
                if ((isOwner || isManager) && employee.email) {
                    return (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleInviteEmployee(employee)}
                            disabled={invitingEmployeeId === employee.id}
                            className="text-xs h-7 px-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-sm border-0"
                            icon={invitingEmployeeId === employee.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        >
                            Invitar
                        </Button>
                    );
                }
                return <span className="text-xs text-gray-400 italic">{employee.email ? 'Sin invitar' : 'Sin email'}</span>;
        }
    };

    return (
        <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Empleados</h1>
                    <p className="text-gray-500 mt-1">Gestiona tu equipo de estilistas y sus permisos</p>
                </div>

                <div className="flex items-center gap-3">
                    {/* Usage Badge */}
                    {(() => {
                        const activeCount = employees.filter(e => e.isActive).length;
                        const limit = getLimit('employees');
                        const isUnlimited = limit > 900000;
                        if (isUnlimited) return null;
                        const isAtLimit = activeCount >= limit;

                        return (
                            <div className={`px-4 py-2 rounded-xl border flex items-center gap-2 ${isAtLimit ? 'bg-red-50 border-red-100 text-red-700' : 'bg-white border-gray-200 text-gray-600'}`}>
                                <span className="text-xs font-bold uppercase tracking-wider">Plan:</span>
                                <span className="font-mono font-medium">{activeCount}/{limit}</span>
                            </div>
                        );
                    })()}

                    <Button
                        onClick={handleCreateClick}
                        variant="primary"
                        icon={<Plus size={18} />}
                        className="shadow-lg shadow-[var(--color-primary)]/20"
                    >
                        Nuevo Empleado
                    </Button>
                </div>
            </div>



            {!salonId ? (
                <Card className="text-center py-20 bg-gray-50/50 border-dashed border-2">
                    <UserCheck className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                    <p className="text-gray-500 mb-6">Configura tu salón para comenzar</p>
                    <Button onClick={() => window.location.href = '/app/onboarding'}>Configurar Salón</Button>
                </Card>
            ) : (
                <>
                    {/* Search Bar */}
                    <div className="mb-6">
                        <Input
                            placeholder="Buscar por nombre, especialidad..."
                            leftIcon={<Search size={18} />}
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="bg-white border-gray-200 shadow-sm hover:border-gray-300 focus:ring-[var(--color-primary)]/20"
                        />
                    </div>

                    {/* Table Container */}
                    <Card variant="white" padding="none" className="overflow-hidden shadow-sm border-gray-100">
                        {isLoading ? (
                            <TableSkeleton rows={5} columns={6} />
                        ) : filteredEmployees.length === 0 ? (
                            <EmptyState
                                icon={UserCheck}
                                title={searchQuery ? 'No se encontraron empleados' : 'No hay empleados'}
                                description={searchQuery ? 'Intenta con otro término de búsqueda o ajusta los filtros.' : 'Comienza agregando a tu primer estilista. Los empleados son esenciales para gestionar la agenda y los servicios.'}
                                action={!searchQuery ? {
                                    label: 'Agregar Primer Empleado',
                                    onClick: handleCreateClick
                                } : undefined}
                                secondaryAction={searchQuery ? {
                                    label: 'Limpiar Búsqueda',
                                    onClick: () => setSearchQuery('')
                                } : undefined}
                            />
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-100">
                                    <thead className="table-header-premium">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Empleado</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contacto</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Especialidades</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Acceso</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-50">
                                        {filteredEmployees.map(employee => (
                                            <tr key={employee.id} className="table-row-hover group">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="flex-shrink-0 h-10 w-10 relative">
                                                            {employee.photoUrl ? (
                                                                <img className="h-10 w-10 rounded-full object-cover ring-2 ring-white shadow-sm" src={employee.photoUrl} alt="" />
                                                            ) : (
                                                                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[var(--color-primary)] to-teal-400 flex items-center justify-center text-white font-bold ring-2 ring-white shadow-sm">
                                                                    {employee.firstName.charAt(0)}{employee.lastName.charAt(0)}
                                                                </div>
                                                            )}
                                                            <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${employee.isActive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                                                        </div>
                                                        <div className="ml-4">
                                                            <div className="font-semibold text-gray-900 group-hover:text-[var(--color-primary)] transition-colors">
                                                                {employee.firstName} {employee.lastName}
                                                            </div>
                                                            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">{employee.role}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">{employee.phone || '-'}</div>
                                                    <div className="text-xs text-gray-500">{employee.email}</div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-wrap gap-1.5 max-w-[200px]">
                                                        {employee.specialties.slice(0, 3).map(s => (
                                                            <span key={s.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                                                                {s.name}
                                                            </span>
                                                        ))}
                                                        {employee.specialties.length > 3 && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-500 border border-gray-200">
                                                                +{employee.specialties.length - 3}
                                                            </span>
                                                        )}
                                                        {employee.specialties.length === 0 && <span className="text-xs text-gray-400 italic">Sin asignar</span>}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${employee.isActive
                                                        ? 'bg-green-50 text-green-700 border-green-100'
                                                        : 'bg-gray-50 text-gray-600 border-gray-200'
                                                        }`}>
                                                        {employee.isActive ? 'Activo' : 'Inactivo'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {getInvitationBadge(employee)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex justify-end gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                                        <Button
                                                            variant="secondary"
                                                            size="sm"
                                                            className="h-8 w-8 p-0"
                                                            onClick={() => handleAvailabilityClick(employee)}
                                                            title="Horarios"
                                                        >
                                                            <Calendar size={14} />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-8 w-8 p-0 text-gray-500 hover:text-[var(--color-primary)]"
                                                            onClick={() => handleEditClick(employee)}
                                                            title="Editar"
                                                        >
                                                            <Edit2 size={14} />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>

                    {/* Mobile Card List (Visible only on small screens) */}
                    {/* ... Note: In a real PWA we'd implement a separate card layout for mobile, relying on the 'hidden md:table' responsive classes above for table visibility ... */}
                </>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
                    <div className="modal-content animate-in fade-in zoom-in-95 duration-200">
                        <div className="modal-header">
                            <h3 className="modal-title">{editingEmployee ? 'Editar Perfil' : 'Nuevo Integrante'}</h3>
                            <button onClick={() => setShowModal(false)} className="modal-close">✕</button>
                        </div>

                        <div className="modal-body">
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="flex justify-center mb-6">
                                    <div className="relative group cursor-pointer">
                                        <div className="h-28 w-28 rounded-full overflow-hidden bg-gray-50 flex items-center justify-center border-2 border-dashed border-gray-300 group-hover:border-[var(--color-primary)] transition-all shadow-inner">
                                            {photoPreview ? (
                                                <img src={photoPreview} alt="Preview" className="h-full w-full object-cover" />
                                            ) : (
                                                <ImageIcon className="h-10 w-10 text-gray-300 group-hover:text-[var(--color-primary)] transition-colors" />
                                            )}
                                        </div>
                                        <label className="absolute bottom-0 right-0 bg-white rounded-full p-2.5 shadow-lg border border-gray-100 hover:bg-gray-50 transition-transform hover:scale-110 cursor-pointer">
                                            <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} disabled={isUploading || isSubmitting} />
                                            {isUploading ? <Loader2 className="h-4 w-4 animate-spin text-[var(--color-primary)]" /> : <Upload className="h-4 w-4 text-gray-600" />}
                                        </label>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <Input label="Nombre" required value={formData.firstName} onChange={e => setFormData({ ...formData, firstName: e.target.value })} disabled={isSubmitting} />
                                    <Input label="Apellido" required value={formData.lastName} onChange={e => setFormData({ ...formData, lastName: e.target.value })} disabled={isSubmitting} />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} disabled={isSubmitting} type="tel" />
                                    <Input label="Email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} disabled={isSubmitting} type="email" />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">* El email es necesario para invitar al empleado al sistema.</p>


                                <div className={`grid ${formData.role === 'STYLIST' ? 'grid-cols-2' : 'grid-cols-1'} gap-4 items-end`}>
                                    <SearchableSelect
                                        label={formData.role === 'STYLIST' ? 'Rol y Comisión' : 'Rol'}
                                        options={[
                                            { value: "STYLIST", label: "Estilista" },
                                            { value: "ASSISTANT", label: "Asistente" },
                                            { value: "RECEPTIONIST", label: "Recepcionista" },
                                            { value: "MANAGER", label: "Gerente" },
                                        ]}
                                        value={formData.role}
                                        onChange={val => setFormData({ ...formData, role: val })}
                                        disabled={isSubmitting}
                                    />
                                    {formData.role === 'STYLIST' && (
                                        <div className="relative">
                                            <Input
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={formData.commissionPercentage}
                                                onChange={e => setFormData({ ...formData, commissionPercentage: e.target.value })}
                                                disabled={isSubmitting}
                                                rightIcon={<span className="text-gray-500 font-bold text-sm">%</span>}
                                                placeholder="Comisión"
                                            />
                                        </div>
                                    )}
                                </div>

                                {formData.role === 'STYLIST' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Especialidades</label>
                                        <div className="flex flex-wrap gap-2 p-3 bg-gray-50/50 rounded-xl border border-gray-100 max-h-32 overflow-y-auto custom-scrollbar">
                                            {specialties.length === 0 ? (
                                                <p className="text-sm text-gray-400 p-2">No hay especialidades registradas en el salón.</p>
                                            ) : (
                                                specialties.map(specialty => (
                                                    <button
                                                        key={specialty.id}
                                                        type="button"
                                                        onClick={() => toggleSpecialty(specialty.id)}
                                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${selectedSpecialtyIds.includes(specialty.id)
                                                            ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)] border-[var(--color-primary)]'
                                                            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                                                            }`}
                                                    >
                                                        {specialty.name}
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-center gap-2 pt-2">
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input type="checkbox" className="sr-only peer" checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[var(--color-primary)]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--color-primary)]"></div>
                                        <span className="ml-3 text-sm font-medium text-gray-700">Empleado Activo</span>
                                    </label>
                                </div>

                                {submitError && (
                                    <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100 flex gap-2">
                                        <AlertCircle size={16} className="mt-0.5" />
                                        {submitError}
                                    </div>
                                )}

                                <div className="modal-actions">
                                    <Button type="button" variant="ghost" onClick={() => setShowModal(false)} disabled={isSubmitting}>Cancelar</Button>
                                    <Button type="submit" variant="primary" isLoading={isSubmitting}>Guardar Empleado</Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {showAvailabilityModal && selectedEmployeeForAvailability && (
                <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowAvailabilityModal(false)}>
                    <div className="modal-content max-w-2xl">
                        <div className="modal-header">
                            <h3 className="modal-title">Disponibilidad: {selectedEmployeeForAvailability.firstName}</h3>
                            <button onClick={() => setShowAvailabilityModal(false)} className="modal-close">✕</button>
                        </div>
                        <div className="modal-body">
                            <EmployeeAvailabilityManager
                                employeeId={selectedEmployeeForAvailability.id}
                                employeeName={`${selectedEmployeeForAvailability.firstName} ${selectedEmployeeForAvailability.lastName}`}
                                employeePhotoUrl={selectedEmployeeForAvailability.photoUrl}
                                overrides={availabilities}
                                onAddOverride={handleAddAvailabilityOverride}
                                onDeleteOverride={handleDeleteAvailabilityOverride}
                                onClose={() => setShowAvailabilityModal(false)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {showPrerequisiteAlert && (
                <PrerequisiteAlertModal
                    isOpen={showPrerequisiteAlert}
                    title="Se requieren especialidades"
                    message="Antes de crear un empleado, debes registrar al menos una especialidad (corte, tinte, etc.) que este empleado pueda realizar."
                    redirectPath="/app/services"
                    redirectLabel="Ir a Especialidades"
                    onClose={() => setShowPrerequisiteAlert(false)}
                />
            )}
        </div>
    );
};

export default EmployeeListPage;
